// R57 — HER OWN WORDS, AND A REFUSED SAVE WAS DRAWN EXACTLY LIKE A SAVED ONE.
//
// THE HUNT is the pointer's, a fifth route on from R51/R52/R53/R54/R55. `/api/journal` has FOUR call
// sites in App.tsx (measured 2026-08-01) and this is the worst instance of the shape found so far,
// because it is the one route on this site where **there is no shelf to check afterwards** — the
// thing being saved is a sentence she wrote, and if it never landed there is no second copy of it
// anywhere in the world.
//
//   * **THE WRITE.** The handler tested `response.ok && data?.entry` and on ANYTHING else fell out
//     of the `try` into the not-signed-in branch, which prepends the entry LOCALLY with a
//     `Date.now()` id. **She writes in her journal, the save is refused, the entry appears in her
//     list looking exactly like a persisted row, and it is gone forever when she closes the tab —
//     and nothing ever said a word.** It is R47's finding on her own words. The route refuses at
//     401, at 400 ("Write something before saving.") and at **HTTP 200 `{ ok: false }`** when
//     `ensureUser` comes back empty — a SIXTH route in this house with that shape
//     (R39/R47/R51/R53/R54/R55).
//   * **THE READ.** `r.ok ? r.json() : { entries: [] }` and then `if (Array.isArray(d.entries))` —
//     **R55's defect exactly, on her journal: the fabricated fallback IS an array, so it sails
//     through the guard that looks like it is protecting her and paints "No entries yet" over
//     everything she has ever written.** And the server half was the one R55 fixed next door:
//     `handleJournals` answered a real `{ entries: [] }` on an empty `ensureUser`, so the lie was on
//     both sides of the wire again.
//   * **THE DELETE.** Honest since R35 (it reads the answer and the row only leaves when the server
//     says it is gone). It is here so there is ONE answer-reader for this route rather than two
//     thirds of one (R53's ruling).
//   * **THE FOURTH SITE** is the angel card's Send to journal, which already has its own three-state
//     chip and an error line; it reads the same answer as everything else now.
//
// **AND THE ROUND FOUND A SECOND ONE UNDERNEATH, WHICH IS WHY `isSessionOnlyId` IS THE FIRST LAW IN
// THIS FILE.** "Is this id a real saved row or a session-only one?" was answered TWICE in App.tsx,
// in two different ways, seventy lines apart:
//
//     the save:    editingSaved = editingId != null && !/^\d+$/.test(editingId)
//     the delete:  sessionOnly  = typeof id === "number" || /^\d{13,}$/.test(String(id))
//
// **A SAVED ENTRY'S ID IS A POSTGRES SERIAL** — `listJournals` hands back `String(r.id)`, so `"42"`
// — which is all digits, so the SAVE side called every real entry session-only. X6's whole
// edit-in-place capability was dead on arrival: clicking a saved entry, changing a word and pressing
// **Update entry POSTed a brand-new row**, leaving the original beside it. Her journal grew a
// duplicate every time she edited anything, and the PATCH route X6 built has never once been
// reached. The delete's rule is the correct one (a `Date.now()` stamp is 13 digits and stays 13
// digits until the year 2286; a serial would need a trillion rows to collide), so it is the one that
// survives — declared ONCE, here, and asked by both (R31: the crossing stops existing rather than
// being watched).
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law), and the answer
// shape is deliberately `video-progress.ts` / `saved-reports.ts`'s rather than a seventh key.

export const journalPath = "/api/journal";
// IT #350/#351 (2026-10-11): an entry saved from OUTSIDE the journal drawer (the Angel room, the file cabinet) reached the server
// and never the drawer, which loads once at sign-in, so a member read "Sent to journal" over an empty journal. A writer outside the
// drawer announces the save under this one name; the drawer re-reads her list from the server when it hears it.
export const JOURNAL_CHANGED_EVENT = "house:journal-changed";
export const journalEntryPath = (id: string) => `${journalPath}/${encodeURIComponent(id)}`;

// **THE ONE DEFINITION.** A session-only entry is one this house minted itself with `Date.now()`
// because the save never landed; everything else came back from the database with a serial id.
export function isSessionOnlyId(id: string | number | null | undefined): boolean {
  if (id === null || id === undefined) return false;
  return /^\d{13,}$/.test(String(id));
}

// The id a session-only entry is minted with, so the minting and the test above are one fact rather
// than two that agree today. `now` is a PARAMETER for the same reason `doFetch` is (R46).
export function sessionOnlyId(now: number = Date.now()): number {
  return now;
}

// FOUR answers, and the two in the middle are the ones a status code cannot tell apart.
//   sign-in — there is no session (401, or no member at all). Nothing is decided about the words.
//   refused — the server DELIBERATELY said no, at 200 or at 4xx/5xx, and it usually said why.
//   offline — the request never landed. Nothing was decided; transport noise (R18).
export type JournalAnswer<T> =
  | { ok: true; entry: T }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

// The three sentences the house owns, handed in by whoever holds the string catalog — this module
// imports nothing and cannot read it (`saved-books.ts`'s fallback-parameter shape).
export type JournalCopy = { signIn: string; failed: string };

type Json = { ok?: boolean; message?: string; entry?: unknown };

// THE READ. **A FAILED READ IS NOT AN EMPTY JOURNAL** (R18): null, so a surface keeps whatever it
// was already showing rather than telling her she has never written anything. The route's own empty
// list is a real `{ entries: [] }` — a member who has not written yet — so the two are told apart by
// the SHAPE, not by the status code, and the fabricated fallback that used to wear that shape is
// gone from both sides of the wire.
//
// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate drives every answer the route can
// give without a browser, a session or a database.
export async function listJournalEntries<T>(doFetch: typeof fetch = fetch): Promise<T[] | null> {
  try {
    const res = await doFetch(journalPath, { cache: "no-store" });
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as { entries?: unknown } | null;
    return Array.isArray(data?.entries) ? (data.entries as T[]) : null;
  } catch {
    return null;
  }
}

// THE WRITE. **A 200 CARRYING NO `entry` IS A REFUSAL, NOT A SUCCESS** (R53's ruling on the same
// question): the row exists in the shape the route returns, so an absent `entry` is a malformed
// answer and there is nothing to put in her list under a saved row's appearance.
//
// The METHOD is decided here, from the id law above, rather than at the call site — that split is
// what let the two definitions drift in the first place. An id that is a real saved row PATCHes it
// in place (X6's law, reachable for the first time); anything else POSTs a new one.
export async function writeJournalEntry<T>(
  draft: { title: string; body: string },
  editingId: string | null = null,
  doFetch: typeof fetch = fetch,
): Promise<JournalAnswer<T>> {
  const editingSaved = editingId != null && editingId !== "" && !isSessionOnlyId(editingId);
  try {
    const res = await doFetch(editingSaved ? journalEntryPath(editingId as string) : journalPath, {
      method: editingSaved ? "PATCH" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft),
    });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as Json;
    if (res.ok && data?.ok !== false && data?.entry !== undefined && data?.entry !== null) {
      return { ok: true, entry: data.entry as T };
    }
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// THE DELETE. R35's law, unchanged and now shared: the row comes off her screen ONLY if the server
// really took it. TRUE only when the server said the row is gone.
export async function removeJournalEntry(id: string, doFetch: typeof fetch = fetch): Promise<boolean> {
  try {
    const res = await doFetch(journalEntryPath(id), { method: "DELETE" });
    const data = (await res.json().catch(() => ({}))) as Json;
    return res.ok && data?.ok !== false;
  } catch {
    return false;
  }
}

// The sentence, derived from the answer rather than typed per surface. **A SUCCESS SAYS NOTHING** —
// the entry appearing in her list IS the answer, and a line under it would be noise (R51/R53/R54/R55
// on the same question). Only a failure has something to tell her, and until this round it had no
// way to tell her at all.
export function journalNote<T>(answer: JournalAnswer<T>, copy: JournalCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}
